// Source packages — telling "the file Stew cut these from" apart from
// "a document nobody has filed yet".
//
// CONSUMER: both (the close-out card, the audit zip, the loan folder).
//
// When a closing package is split, the original is uploaded too, so
// nothing is ever lost. Every piece cut from it carries
// `split_from_document_id` pointing at the package's own row
// (migration 20260902013850). This module is the one place that reads
// that link back into a shape the surfaces can use, so that:
//
//   * the card shows the package as "Source package — split into 17
//     documents", not as unfinished work with a "File under…" dropdown;
//   * the audit zip files it under "Source packages/", labelled, rather
//     than handing the auditor every document twice as "Other";
//   * a second drop of the same package can be recognised before it
//     files everything twice.
//
// Pure: takes document rows, returns groups. No database.

/** The fields this module reads — every documents row has them. */
export interface SplitLinkedDocument {
  id: string;
  filename: string;
  file_size_bytes: number | null;
  document_type: string | null;
  archived: boolean;
  uploaded_at: string;
  split_from_document_id: string | null;
}

export interface SourcePackage<D extends SplitLinkedDocument = SplitLinkedDocument> {
  document: D;
  /** Live documents cut from it. */
  piece_count: number;
  /** Of those, how many are filed against a checklist requirement. */
  filed_count: number;
}

export interface PartitionedDocuments<D extends SplitLinkedDocument> {
  /** Documents that other live documents were cut from. */
  source_packages: SourcePackage<D>[];
  /** Everything else that was passed in. */
  others: D[];
}

/**
 * Splits a record's live documents into source packages and the rest.
 *
 * A document is a source package when at least one OTHER live document
 * points at it. A package whose every piece has since been removed is
 * just a document again — it goes back to being an ordinary unfiled
 * file, because that is what the reviewer would now want to file or
 * remove by hand. Archived rows are ignored on both sides.
 */
export function partitionSourcePackages<D extends SplitLinkedDocument>(
  docs: readonly D[],
): PartitionedDocuments<D> {
  const live = docs.filter((d) => !d.archived);
  const pieceCount = new Map<string, number>();
  const filedCount = new Map<string, number>();
  for (const d of live) {
    if (!d.split_from_document_id) continue;
    pieceCount.set(d.split_from_document_id, (pieceCount.get(d.split_from_document_id) ?? 0) + 1);
    if (d.document_type) {
      filedCount.set(
        d.split_from_document_id,
        (filedCount.get(d.split_from_document_id) ?? 0) + 1,
      );
    }
  }
  const source_packages: SourcePackage<D>[] = [];
  const others: D[] = [];
  for (const d of live) {
    const pieces = pieceCount.get(d.id) ?? 0;
    if (pieces > 0) {
      source_packages.push({
        document: d,
        piece_count: pieces,
        filed_count: filedCount.get(d.id) ?? 0,
      });
    } else {
      others.push(d);
    }
  }
  return { source_packages, others };
}

export interface PackagePieceGrouping<D extends SplitLinkedDocument> {
  /** Each source package's own id, mapped to the live pieces cut from it. */
  childrenByParent: Map<string, D[]>;
  /** Every piece id across every package — the set a caller filters a
   *  flat list against to stop rendering them a second time at the top
   *  level. */
  childIds: Set<string>;
}

/**
 * The reverse index of partitionSourcePackages' source_packages list:
 * which live documents ARE the pieces, grouped by which package they
 * were cut from.
 *
 * Built for "default to ONE document, allow both" (Grant, 2026-09-20):
 * a signed package renders as its combined copy by default, with the
 * pieces cut from it (the e-sign auto-split, or a hand-split closing
 * package) one click away rather than N extra rows for one signature
 * event. This only changes how a UI GROUPS what it already has — every
 * piece is still an ordinary, fully live document; nothing here hides
 * anything from a data-layer read (a checklist, an export, the audit
 * zip all still see every piece exactly as before).
 */
export function groupPackagePieces<D extends SplitLinkedDocument>(
  docs: readonly D[],
): PackagePieceGrouping<D> {
  const { source_packages, others } = partitionSourcePackages(docs);
  const parentIds = new Set(source_packages.map((p) => p.document.id));
  const childrenByParent = new Map<string, D[]>();
  for (const doc of others) {
    if (doc.split_from_document_id && parentIds.has(doc.split_from_document_id)) {
      const list = childrenByParent.get(doc.split_from_document_id) ?? [];
      list.push(doc);
      childrenByParent.set(doc.split_from_document_id, list);
    }
  }
  const childIds = new Set(
    Array.from(childrenByParent.values()).flatMap((list) => list.map((d) => d.id)),
  );
  return { childrenByParent, childIds };
}

/**
 * The document a freshly picked file most likely IS a second copy of —
 * same name and same size on the same record — or null.
 *
 * Name alone is not enough ("Closing Package.pdf" is what every lender
 * calls it); size alone is not enough (two 3-page disclosures). Both
 * together, on the same loan, is a duplicate until the person says
 * otherwise. Deliberately loose on case and surrounding whitespace,
 * which is how the same file gets renamed by a download.
 */
export function findLikelyDuplicate<D extends SplitLinkedDocument>(
  picked: { name: string; size: number },
  docs: readonly D[],
): D | null {
  const wanted = picked.name.trim().toLowerCase();
  for (const d of docs) {
    if (d.archived) continue;
    if (d.file_size_bytes !== picked.size) continue;
    if (d.filename.trim().toLowerCase() !== wanted) continue;
    return d;
  }
  return null;
}
